import { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { format, formatDistanceToNow } from 'date-fns'
import {
  LinkIcon,
  TrashIcon,
  CreditCardIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'

const numberFormatter = new Intl.NumberFormat('en-US')

function DisconnectedAccountCard({
  account,
  onReconnect,
  onDelete,
  onOpenStripePortal
}) {
  const { t } = useTranslation('settings')
  const [loadingPortal, setLoadingPortal] = useState(false)
  const [reconnecting, setReconnecting] = useState(false)

  const handleReconnect = async (e) => {
    e.stopPropagation()
    setReconnecting(true)
    try {
      await onReconnect(account.id)
    } finally {
      setReconnecting(false)
    }
  }

  const handleDelete = (e) => {
    e.stopPropagation()
    onDelete(account)
  }

  const handleOpenStripePortal = async (e) => {
    e.stopPropagation()
    setLoadingPortal(true)
    try {
      await onOpenStripePortal(account.id)
    } finally {
      setLoadingPortal(false)
    }
  }

  return (
    <div className="card border-2 border-gray-200 bg-gray-50 opacity-75">
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-4">
          {account.profile_picture_url ? (
            <img
              src={account.profile_picture_url}
              alt={account.username}
              className="w-16 h-16 rounded-full object-cover filter grayscale"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-gray-300 flex items-center justify-center">
              <span className="text-2xl">👤</span>
            </div>
          )}
          <div>
            <h3 className="text-lg font-semibold text-gray-700">
              {account.name || account.username}
            </h3>
            <p className="text-sm text-gray-500">@{account.username}</p>
            <div className="flex items-center space-x-4 mt-2">
              <span className="text-xs text-gray-400">
                {t('accounts.account.stats.followers', { count: numberFormatter.format(account.followers_count || 0) })}
              </span>
              <span className="text-xs text-gray-400">
                {t('accounts.account.stats.posts', { count: numberFormatter.format(account.media_count || 0) })}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end space-y-2">
          <span className="px-2 py-1 text-xs font-medium bg-gray-600 text-white rounded">
            {t('accounts.account.badge.disconnected')}
          </span>
        </div>
      </div>

      {/* Warning about disconnected state */}
      <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-start space-x-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="text-yellow-800 font-medium">{t('accounts.disconnected.warning.title')}</p>
            <p className="text-yellow-700 mt-1">{t('accounts.disconnected.warning.message')}</p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-4 flex flex-col space-y-2">
        <div className="flex items-center space-x-2">
          <button
            onClick={handleReconnect}
            disabled={reconnecting}
            className="flex-1 px-3 py-2 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors border border-primary-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={t('accounts.account.button.reconnect')}
          >
            <LinkIcon className="h-4 w-4" />
            <span>{reconnecting ? t('accounts.account.button.reconnecting') : t('accounts.account.button.reconnect')}</span>
          </button>
          <button
            onClick={handleOpenStripePortal}
            disabled={loadingPortal}
            className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 rounded-lg transition-colors border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            aria-label={t('accounts.billing.viewInvoices')}
          >
            <CreditCardIcon className="h-4 w-4" />
            <span>{loadingPortal ? t('accounts.billing.loading') : t('accounts.billing.viewInvoices')}</span>
          </button>
        </div>
        <button
          onClick={handleDelete}
          className="w-full px-3 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-200 flex items-center justify-center space-x-2"
          aria-label={t('accounts.delete.button')}
        >
          <TrashIcon className="h-4 w-4" />
          <span>{t('accounts.delete.button')}</span>
        </button>
      </div>

      {/* Last disconnected info */}
      {account.disconnected_at && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">{t('accounts.disconnected.details.disconnectedAt')}</span>
            <span className="font-medium text-gray-700">
              {formatDistanceToNow(new Date(account.disconnected_at), { addSuffix: true })}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

DisconnectedAccountCard.propTypes = {
  account: PropTypes.shape({
    id: PropTypes.string.isRequired,
    username: PropTypes.string.isRequired,
    name: PropTypes.string,
    profile_picture_url: PropTypes.string,
    followers_count: PropTypes.number,
    media_count: PropTypes.number,
    disconnected_at: PropTypes.string
  }).isRequired,
  onReconnect: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onOpenStripePortal: PropTypes.func.isRequired
}

export default DisconnectedAccountCard